'use client';

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type CSSProperties,
} from 'react';
import Link from 'next/link';
import useEmblaCarousel from 'embla-carousel-react';
import { useTranslations } from 'next-intl';

import { CaretUp } from 'assets/icons';
import type { StudyCategorySlug } from 'content/science/studyTypes';
import CategoryFilterDropdown, {
  type FilterOption,
} from '../listing/CategoryFilterDropdown';
import ASK_QUESTIONS, {
  ASK_CATEGORY_LABEL,
  type AskCategoryId,
  type AskQuestion,
} from './askData';
import {
  ART_RAMP,
  ASK_ID_FOR_SLUG,
  CATEGORY_DOT,
  SHARED_SLUG,
} from './askTokens';
import styles from './AskOurDataSection.module.css';

/* Ask Our Data — the Bytes section, carrying questions instead of snapshots.
 *
 * The band, carousel, dropdown, controls and type ramp are lifted from
 * SnapshotCarousel so the closing section of /science/studies reads as it
 * always did; only the payload changed. The art plate stands in for a
 * photograph that has not been shot yet.
 *
 * Question and answer are line-clamped because the card body carries three
 * fields: with free-running copy the rule above the provenance line lands at a
 * different height on every card.
 *
 * A question can belong to more than one subject, so the dropdown counts sum
 * past the question total by design and the filter matches on membership. The
 * pill on the art names the primary subject only, which is also the art's hue.
 *
 * One view, not two: the rail carries the full set and the arrows walk it, so
 * there is no expanded state a reader can be stranded in.
 *
 * The shared CategoryFilterDropdown speaks StudyCategorySlug, so the Ask ids
 * are translated at this boundary through askTokens' SHARED_SLUG and its
 * inverse rather than by making that component generic — it has two other
 * callers. Labels still come from the Ask ids, so the copy is unaffected.
 */

type Filter = AskCategoryId | 'all';

/* Subject order is authored, biggest first, so the menu reads as a hierarchy
   rather than as an alphabet. */
const SUBJECTS: AskCategoryId[] = [
  'heart',
  'sleep',
  'metabolic',
  'women',
  'environment',
];

/* The rail's questions. Passed in by the route, which merges the CMS over the
   vendored extract — see mergeAskQuestions. Defaults to the extract so the
   component still renders if it is ever mounted without them. */
interface AskOurDataSectionProps {
  questions?: AskQuestion[];
}

const AskOurDataSection = ({ questions: supplied }: AskOurDataSectionProps = {}) => {
  const t = useTranslations('science');
  const [filter, setFilter] = useState<Filter>('all');

  const questions = useMemo(
    () => [...(supplied ?? ASK_QUESTIONS)].sort((a, b) => a.order - b.order),
    [supplied],
  );

  /* Counted from the questions themselves. buildFilterOptions cannot be used
     here: it assumes one category per item, and these carry several. */
  const filterOptions = useMemo<FilterOption[]>(
    () => [
      { slug: 'all', label: t('science.filter.all'), count: questions.length },
      ...SUBJECTS.map((s) => ({
        slug: SHARED_SLUG[s],
        label: t(`science.askCategory.${s}`),
        count: questions.filter((q) => q.categories.includes(s)).length,
      })).sort((a, b) => b.count - a.count),
    ],
    [questions, t],
  );

  const filtered = useMemo(
    () =>
      filter === 'all'
        ? questions
        : questions.filter((q) => q.categories.includes(filter)),
    [filter, questions],
  );

  const handleFilter = (slug: StudyCategorySlug | 'all') =>
    setFilter(slug === 'all' ? 'all' : ASK_ID_FOR_SLUG[slug]);

  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    align: 'start',
    slidesToScroll: 1,
  });
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => {
      setCanPrev(emblaApi.canScrollPrev());
      setCanNext(emblaApi.canScrollNext());
    };
    onSelect();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('reInit', onSelect);
    };
  }, [emblaApi, filter]);

  return (
    <section
      className={styles.section}
      id="questions"
      aria-labelledby="questions-title"
    >
      <div className={styles.inner}>
        <div className={styles.headerRow}>
          <h2 className={styles.heading} id="questions-title">
            {t('science.ask.heading')}
          </h2>
        </div>

        <p className={styles.subline}>{t('science.ask.subline')}</p>

        {/* Filter and arrows travel together in the DOM and are placed apart
            by CSS — the filter beside the heading on a laptop, the arrows
            directly above the cards on both. Done in CSS rather than by
            branching on a breakpoint so there is no first-paint reflow. */}
        <div className={styles.controlRow}>
          <div className={styles.filterSlot}>
            <CategoryFilterDropdown
              active={filter === 'all' ? 'all' : SHARED_SLUG[filter]}
              options={filterOptions}
              onChange={handleFilter}
              variant="dark"
            />
          </div>

          {filtered.length ? (
            <div className={styles.controls}>
              <button
                className={styles.navBtn}
                type="button"
                onClick={scrollPrev}
                disabled={!canPrev}
                aria-label={t('science.ask.prevQuestion')}
              >
                <CaretUp
                  aria-hidden="true"
                  style={{ transform: 'rotate(-90deg)' }}
                />
              </button>
              <button
                className={styles.navBtn}
                type="button"
                onClick={scrollNext}
                disabled={!canNext}
                aria-label={t('science.ask.nextQuestion')}
              >
                <CaretUp
                  aria-hidden="true"
                  style={{ transform: 'rotate(90deg)' }}
                />
              </button>
            </div>
          ) : null}
        </div>

        {filtered.length === 0 ? (
          <p className={styles.empty}>{t('science.ask.emptySubject')}</p>
        ) : (
          <div className={styles.viewport} ref={emblaRef}>
            <div className={styles.track}>
              {/* The whole filtered set, not a first page of it. With the
                  reveal gone the rail is the only way to any of these, so
                  slicing it here would make the remainder unreachable. */}
              {filtered.map((q) => (
                <div className={styles.slide} key={q.slug}>
                  <QuestionCard question={q} />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default AskOurDataSection;

/* One question, as a card on the rail. */
const QuestionCard = ({ question }: { question: AskQuestion }) => {
  const primary = question.categories[0];
  const ramp = ART_RAMP[primary];

  return (
    <Link className={styles.card} href={`/science/ask/${question.slug}`}>
      <div className={styles.imageWrap}>
        {/* The gradient stays underneath rather than being replaced. It is what
            fills the frame while the photograph decodes, and what remains if a
            question ever ships without art — the card keeps its subject's hue
            either way instead of showing a grey hole. */}
        <div
          className={styles.art}
          style={
            {
              '--art-from': ramp.from,
              '--art-to': ramp.to,
            } as CSSProperties
          }
          aria-hidden="true"
        />
        {question.coverImageUrl ? (
          /* alt="" when nobody has written one, NOT the question.
             The image sits inside the same link as the <h3> below it, so an
             alt of the question made a screen reader announce the same
             sentence twice — once for the picture, once for the heading. The
             cover carries no information the heading does not, which is the
             definition of decorative. An authored alt still wins: the moment
             someone describes the art itself, it is no longer decorative and
             saying so is worth more than the filename. */
          <img
            className={styles.cover}
            src={question.coverImageUrl}
            alt={question.coverImageAlt?.trim() || ''}
            loading="lazy"
            decoding="async"
          />
        ) : null}
        <span className={styles.pill}>
          <span
            className={styles.swatch}
            style={{ background: CATEGORY_DOT[primary] }}
            aria-hidden="true"
          />
          <span className={styles.pillLabel}>
            {ASK_CATEGORY_LABEL[primary]}
          </span>
        </span>
      </div>
      <div className={styles.body}>
        <h3 className={styles.question}>{question.question}</h3>
        <p className={styles.answer}>{question.answer}</p>
        <p className={styles.prov}>
          <span className={styles.cohort}>{question.cohort}</span>
          <span className={styles.sep} aria-hidden="true">
            ·
          </span>
          <span className={styles.ref}>{question.reference}</span>
        </p>
      </div>
    </Link>
  );
};
